<script lang="ts">
  import GridLines from '#lib/components/GridLines.svelte';
  import GridContent from '#lib/components/GridContent.svelte';
  import GridInner from '#lib/components/GridInner.svelte';
  import Star from '#lib/components/Star.svelte';
  import { submitNewsletter, LeadApiError } from '#lib/api/leads.js';

  type SubscribeState = 'idle' | 'submitting' | 'success' | 'error';
  let subscribeState = $state<SubscribeState>('idle');
  let subscribeEmail = $state('');
  let subscribeError = $state('');

  async function handleSubscribe(e: Event) {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(subscribeEmail)) {
      subscribeError = 'Please enter a valid email';
      subscribeState = 'error';
      return;
    }
    subscribeState = 'submitting';
    subscribeError = '';
    try {
      await submitNewsletter(subscribeEmail.trim());
      subscribeState = 'success';
    } catch (err) {
      subscribeState = 'error';
      subscribeError = err instanceof LeadApiError ? err.message : 'Something went wrong.';
    }
  }

  // Footer navigation structure - 6 sections across columns
  const footerColumns = [
    // Column 1: Product + CTA cluster
    [
      {
        title: 'Product',
        href: '/product',
        links: [
          { label: 'Overview', href: '/product' },
          { label: 'Hub & Surfaces', href: '/product/hub' },
          { label: 'Agent Builder', href: '/product/builder' },
          { label: 'Security & Governance', href: '/product/security' },
          { label: 'Connectors & Integrations', href: '/product/connectors' },
          { label: 'Admin & Observability', href: '/product/admin' },
        ]
      },
      {
        ctaCluster: true,
        links: []
      }
    ],
    // Column 2: Why Corral + Use Cases
    [
      {
        title: 'Why Corral',
        href: '/why-corral',
        links: [
          { label: 'The Problem', href: '/why-corral#the-problem' },
          { label: 'The Thesis', href: '/why-corral#the-thesis' },
          { label: 'Three Paths', href: '/why-corral#three-paths' },
          { label: 'For the Engineer', href: '/why-corral#for-the-engineer' },
          { label: 'Sovereignty', href: '/why-corral#sovereignty' },
        ]
      },
      {
        title: 'Use Cases',
        links: [
          { label: 'Ops & Product Teams', href: '/use-cases/ops-product' },
          { label: 'Developers & Engineers', href: '/use-cases/developers' },
          { label: 'Service Providers', href: '/use-cases/service-providers' },
          { label: 'Product Companies', href: '/use-cases/product-companies' },
          { label: 'IT & Security', href: '/use-cases/it-security' },
        ]
      }
    ],
    // Column 3: Learn + Company + Legal
    [
      {
        title: 'Learn',
        href: '/learn',
        links: [
          { label: 'Documentation', href: '/docs' },
          { label: 'Guides', href: '/learn' },
          { label: 'Blog', href: '/news' },
        ]
      },
      {
        title: 'Legal',
        links: [
          { label: 'Terms', href: '/terms/tos' },
          { label: 'Privacy', href: '/terms/privacy' },
          { label: 'Compliance', href: '/terms/compliance' },
        ]
      }
    ]
  ];
</script>

<footer class="footer bg-black relative">
  <GridLines variant="white" />

  <!-- Top horizontal line -->
  <div class="hidden lg:block absolute top-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <!-- Half stars facing down at grid line intersections -->
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] top-0" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] top-0" />

  <section class="pt-8 pb-8">
    <GridContent>
      <GridInner>
      <div class="relative">
      <div class="flex flex-col md:flex-row md:justify-between gap-8">
        {#each footerColumns as column}
          <div class="flex flex-col gap-8">
            {#each column as section}
              <div>
                {#if 'ctaCluster' in section && section.ctaCluster}
                  <div class="flex flex-col gap-3">
                    <a href="/company/contact" class="inline-block self-start font-mono font-light text-[16px] text-white hover:text-[#878787] transition-colors border border-white hover:border-[#878787] px-4 py-2">
                      Contact Us
                    </a>
                    <a href="/partners" class="inline-block self-start font-mono font-light text-[16px] text-white hover:text-[#878787] transition-colors border border-white hover:border-[#878787] px-4 py-2">
                      Apply for Design Partners
                    </a>
                    {#if subscribeState === 'success'}
                      <p class="font-mono text-[14px] text-[#878787]">Subscribed!</p>
                    {:else}
                      <form onsubmit={handleSubscribe} class="flex gap-0">
                        <input
                          type="email"
                          bind:value={subscribeEmail}
                          placeholder="Subscribe: you@company.com"
                          disabled={subscribeState === 'submitting'}
                          class="flex-1 min-w-0 bg-[#171719] border border-[#333335] px-4 py-2 font-mono text-[14px] text-white placeholder:text-[#5C5855] focus:outline-none focus:border-[#ee6018] transition-colors disabled:opacity-50"
                        />
                        <button
                          type="submit"
                          disabled={subscribeState === 'submitting'}
                          class="bg-[#ee6018] text-white font-mono text-[14px] px-4 py-2 hover:bg-[#d55515] transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {subscribeState === 'submitting' ? '...' : 'Subscribe'}
                        </button>
                      </form>
                      {#if subscribeState === 'error' && subscribeError}
                        <p class="font-mono text-[12px] text-red-400 mt-1">{subscribeError}</p>
                      {/if}
                    {/if}
                  </div>
                {:else if 'disabled' in section && section.disabled}
                  <h3 class="font-mono font-light text-[16px] mb-4 text-[#4a4a4a]">{section.title}</h3>
                {:else if 'href' in section && section.href}
                  <a href={section.href} class="font-mono font-light text-[16px] mb-4 text-white hover:text-[#878787] transition-colors block">{section.title}</a>
                {:else}
                  <h3 class="font-mono font-light text-[16px] mb-4 text-white">{section.title}</h3>
                {/if}
                {#if section.links.length > 0}
                  <ul class="flex flex-col gap-3">
                    {#each section.links as link}
                      <li>
                        {#if 'disabled' in link && link.disabled}
                          <span class="font-mono font-light text-[16px] text-[#4a4a4a] cursor-not-allowed">
                            {link.label}
                          </span>
                        {:else}
                          <a href={link.href} class="font-mono font-light text-[16px] text-[#878787] hover:text-white transition-colors">
                            {link.label}
                          </a>
                        {/if}
                      </li>
                    {/each}
                  </ul>
                {/if}
              </div>
            {/each}
          </div>
        {/each}
      </div>

      <!-- Bottom Bar - Lockup + Copyright -->
      <div class="mt-4 mb-2 pt-2 flex items-center gap-4">
        <img src="/shared/images/logos/mark-corral-light.svg" alt="Corral" class="h-6 w-auto" />
        <p class="font-mono font-light text-[14px] text-[#878787]">© 2026 Corral AI. All rights reserved.</p>
      </div>
      </div>
      </GridInner>
    </GridContent>
  </section>

  <!-- Footer style image - truncated to half height -->
  <div class="relative">
    <!-- Horizontal line at top of image area -->
    <div class="hidden lg:block absolute top-0 left-0 right-0 h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>

    <GridContent>
      <div class="overflow-hidden relative" style="padding-bottom: 18%;">
        <img
          src="/shared/images/elements/footer-style.png"
          alt=""
          class="w-full absolute top-0 left-0"
          aria-hidden="true"
        />
      </div>
    </GridContent>
  </div>
</footer>
